/* What a row of the connections table says, as decisions rather than JSX.
 *
 * The screen is a **worklist**, not a catalogue: a handful of sources, each with a state and a next
 * action, and the question asked of it is "which of these needs me". That is why it is a table now
 * rather than a card grid — a table puts five sources' states in one vertical scan where cards put
 * them at five different heights — and it is why everything here is about ordering and absence.
 *
 * Two rules shape all of it.
 *
 * **Sources needing action come first, and the lede says so, so it has to be true.** `byAttention`
 * is the mechanism; this file's `ATTENTION_ORDER` is where the ranking is argued.
 *
 * **Every reading is a discriminated union, never a number with a sentinel.** A caller cannot render
 * an absent figure as a zero without first naming which absence it is — the shape
 * `features/catalog-browser/model/catalogue.ts` established and the reason a harvest that has not
 * started does not read `0 of 0`.
 */

import type { Connection, ConnectionSummary } from '@/shared/api/generated/catalog';

/* ------------------------------------------------------------------ ordering

 * Failed first, then never-verified, then out of service, then healthy.
 *
 * The ranking is by *what a reader has to do*, not by severity. `failed` is first because something
 * this platform relied on has stopped working. `registered` is second because it is an unfinished
 * onboarding step — nobody has tried the credential, so nobody knows. `disabled` is third and
 * deliberately not last: it is somebody's decision rather than a fault, so it is not urgent, but a
 * source silently out of service for a month is a monitoring gap and sorting it below healthy ones
 * would bury it. `verified` is last because there is nothing to do.
 */
const ATTENTION_ORDER = {
  failed: 0,
  registered: 1,
  disabled: 2,
  verified: 3,
} as const satisfies Record<Connection['state'], number>;

export function byAttention(left: Connection, right: Connection): number {
  const delta = ATTENTION_ORDER[left.state] - ATTENTION_ORDER[right.state];
  /* Name as the tiebreaker, so the order is total and stable across refetches. Without it two
   * sources in one state swap places whenever the server's ordering shifts, which reads as the
   * table rearranging itself for no reason while somebody is aiming at a row. */
  return delta !== 0 ? delta : left.name.localeCompare(right.name);
}

/** Whether a source is one of the ones the lede promises come first. */
export function needsAction(connection: Connection): boolean {
  return connection.state === 'failed' || connection.state === 'registered';
}

/* ------------------------------------------------------------------ the status cell

 * The status column carries two different kinds of fact and must not blend them: what a person
 * decided about the source, and what the platform is doing to it right now.
 *
 * A **running harvest wins the cell** — "Harvesting, 312 of 504" is the most useful thing a reader
 * can be told about a source at that moment, and it is temporary, so it does not erase the state
 * underneath it: `stateOf` is still rendered as the row's badge tone. A harvest cannot be running on
 * a disabled source (every path refuses one), so the two cannot contradict each other.
 *
 * `discovered === 0` is **not** a measured emptiness. A harvest reports its discovery count once it
 * has enumerated the source, so a run that has started and not yet counted has zero — and rendering
 * `0 of 0` would claim an empty database. That is the reading `catalogue.ts` names `starting`.
 */
export type HarvestReading =
  | { readonly kind: 'none' }
  | { readonly kind: 'starting' }
  | { readonly kind: 'running'; readonly observed: number; readonly discovered: number }
  | { readonly kind: 'failed' }
  | { readonly kind: 'partial' };

const RUNNING_STATES = new Set(['pending', 'running']);

/** What the most recent harvest of this source says, if anything.
 *
 * `undefined` for a summary the screen could not read — a failed `/connections/summary` must not put
 * "no harvest" on every row, because that is a claim about the tenant's estate made to report our
 * own failed request.
 */
export function harvestReading(summary: ConnectionSummary | undefined): HarvestReading {
  /* One local for both absences, because they mean the same thing here and only here: a summary we
   * could not read and a source that has never been harvested both leave the status cell with
   * nothing to add. They are *not* interchangeable to the caller — the screen reports a failed
   * summary read separately, under the table — which is why that distinction lives there and not in
   * this reading. */
  const state = summary?.harvest_state ?? null;
  if (state === null) return { kind: 'none' };
  if (RUNNING_STATES.has(state)) {
    return summary === undefined || summary.discovered === 0
      ? { kind: 'starting' }
      : { kind: 'running', observed: summary.observed, discovered: summary.discovered };
  }
  if (state === 'failed') return { kind: 'failed' };
  /* `partial` is its own reading rather than a success, because a harvest that reached the source
   * and could not read every object produced a catalogue incomplete in ways nobody has been told
   * about — and this platform deliberately refuses to infer absence from one. Rendering it as done
   * would erase the fact the state exists to carry. `succeeded` and `cancelled` fall through to
   * `none`: neither is happening now, and the row's own state plus the last-checked column are what
   * a reader wants once a harvest is over.
   */
  if (state === 'partial') return { kind: 'partial' };
  return { kind: 'none' };
}

/* ------------------------------------------------------------------ last checked

 * How long since a verification was *attempted*, in whole units, largest that fits.
 *
 * Four readings, and the two absences are not the same. `never` is a fact about the source — nobody
 * has tested it — and is what every connection registered before the column existed honestly says.
 * `unreadable` would be a fact about our own request, and there is no such case here because the
 * field travels on the connection the row is already rendering: if the row exists, the field was
 * read. Stated so a future caller does not add a third absence by accident.
 *
 * `justNow` rather than "0 minutes", because a zero here would be the only number on the screen that
 * means "less than one" rather than "none".
 */
export type Checked =
  | { readonly kind: 'never' }
  | { readonly kind: 'justNow' }
  | { readonly kind: 'minutes'; readonly count: number }
  | { readonly kind: 'hours'; readonly count: number }
  | { readonly kind: 'days'; readonly count: number };

const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export function lastChecked(connection: Connection, now: Date): Checked {
  const raw = connection.last_checked_at;
  if (raw === null) return { kind: 'never' };
  const at = new Date(raw);
  /* An unparseable instant reads as never rather than as `Invalid Date` rendered into a cell. The
   * contract types this as a date-time, so this is defence against a shape the contract forbids —
   * which is exactly what a guard is for. */
  if (Number.isNaN(at.getTime())) return { kind: 'never' };

  const elapsed = now.getTime() - at.getTime();
  /* A future instant reads as just now rather than as a negative age. Clock skew between this
   * platform and a browser is real and unbounded, and "in -1 hours" is a rendering bug that looks
   * like data — the call `activityWhen` and `describeAge` already make. */
  if (elapsed < MINUTE) return { kind: 'justNow' };
  if (elapsed < HOUR) return { kind: 'minutes', count: Math.floor(elapsed / MINUTE) };
  if (elapsed < DAY) return { kind: 'hours', count: Math.floor(elapsed / HOUR) };
  return { kind: 'days', count: Math.floor(elapsed / DAY) };
}

/* ------------------------------------------------------------------ tallies

 * How many sources are in each state, for the line above the table. Four counters rather than one
 * "needs attention" figure, because the screen composes four ICU messages whose empty branches
 * contribute nothing: "3 verified" reads as an answer where "3 verified, 0 failed" reads as a
 * problem.
 */
export interface StateTally {
  readonly verified: number;
  readonly unverified: number;
  readonly failed: number;
  readonly disabled: number;
}

export function tally(connections: readonly Connection[]): StateTally {
  let verified = 0;
  let unverified = 0;
  let failed = 0;
  let disabled = 0;
  for (const connection of connections) {
    if (connection.state === 'verified') verified += 1;
    else if (connection.state === 'registered') unverified += 1;
    else if (connection.state === 'failed') failed += 1;
    else disabled += 1;
  }
  return { verified, unverified, failed, disabled };
}
